import { Check, Minus, Lock, Link2, Link2Off, FileX2, LayoutPanelTop } from 'lucide-react';

const ACTIONS = ['view', 'edit', 'delete'];

export function StatusBadge({ page, isAr }) {
  if (!page.exists) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-red-500/10 border border-red-500/30 text-red-300" title={isAr ? 'المسار غير موجود في التطبيق (حُذف أو لم يُنشأ)' : 'Route not found in app'}>
        <FileX2 className="h-3 w-3" />{isAr ? 'غير موجودة' : 'Missing'}
      </span>
    );
  }
  if (!page.linked) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300" title={isAr ? 'الصفحة موجودة لكن لا يوجد زر يوصل إليها من لوحة التحكم' : 'Page exists but no button links to it'}>
        <Link2Off className="h-3 w-3" />{isAr ? 'غير مربوطة' : 'Unlinked'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" title={isAr ? 'موجودة ومربوطة بزر في لوحة التحكم' : 'Exists and linked'}>
      <Link2 className="h-3 w-3" />{isAr ? 'مربوطة' : 'Linked'}
    </span>
  );
}

function Cell({ pageId, action, value, base, hasBase, capable, disabled, onToggle, isAr }) {
  if (!capable) {
    return (
      <div className="flex justify-center" title={isAr ? `لا يوجد زر ${action === 'edit' ? 'تعديل' : 'حذف'} في هذه الصفحة` : `No ${action} control on this page`}>
        <span className="w-7 h-7 rounded-lg border border-dashed border-white/10 flex items-center justify-center text-white/20"><Minus className="h-3.5 w-3.5" /></span>
      </div>
    );
  }
  const overridden = hasBase && value !== base;
  const cls = value
    ? overridden ? 'bg-sky-500/25 border-sky-400 text-sky-200' : 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300'
    : overridden ? 'bg-rose-500/15 border-rose-500/60 text-rose-300' : 'bg-white/[0.03] border-white/10 text-white/30 hover:border-white/30';
  return (
    <div className="flex justify-center">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onToggle(pageId, action)}
        className={`relative w-7 h-7 rounded-lg border flex items-center justify-center transition-colors ${cls} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
        data-testid={`perm-cell-${pageId}-${action}`}
        title={overridden ? (isAr ? (value ? 'ممنوحة استثنائياً لهذا الموظف' : 'محجوبة استثنائياً عن هذا الموظف') : (value ? 'Granted (override)' : 'Revoked (override)')) : ''}
      >
        {value ? <Check className="h-4 w-4" /> : <span className="w-2 h-2 rounded-full bg-current" />}
        {overridden && <span className="absolute -top-1 -end-1 text-[9px] font-black leading-none px-1 rounded bg-black/80 border border-white/20">{value ? '+' : '−'}</span>}
      </button>
    </div>
  );
}

export default function PermissionRow({ page, perms, roleBase, sectionOpen, onToggle, isAr }) {
  const cur = perms?.[page.id] || {};
  const base = roleBase?.permissions?.[page.id];
  const hasBase = Boolean(roleBase);
  const isTab = page.id.includes('#');
  return (
    <div
      className={`grid grid-cols-[1fr_52px_52px_52px] items-center gap-1 py-1.5 border-b border-white/5 ${!sectionOpen ? 'opacity-60' : ''}`}
      style={{ paddingInlineStart: `${page.depth * 18}px` }}
      data-testid={`perm-row-${page.id}`}
    >
      <div className="min-w-0 flex items-center gap-2 flex-wrap">
        {page.depth > 0 && <span className="text-white/20 text-xs">↳</span>}
        {isTab && <LayoutPanelTop className="h-3 w-3 text-violet-300 shrink-0" title={isAr ? 'تبويب داخل الصفحة' : 'In-page tab'} />}
        <span className={`text-xs ${page.depth === 0 ? 'font-bold text-white' : 'text-[#d4d4d8]'}`}>{isAr ? page.ar : page.en}</span>
        <code className="text-[9.5px] text-white/30 font-mono truncate max-w-[220px]" title={page.id}>{page.id}</code>
        <StatusBadge page={page} isAr={isAr} />
        {!sectionOpen && <Lock className="h-3 w-3 text-white/30" title={isAr ? 'الدخول للقسم محجوب — تفعيل «عرض» هنا يفتح القسم تلقائياً' : 'Section access off — enabling view here opens the section automatically'} />}
      </div>
      {ACTIONS.map((a) => (
        <Cell
          key={a}
          pageId={page.id}
          action={a}
          value={Boolean(cur[a])}
          base={Boolean(base?.[a])}
          hasBase={hasBase}
          capable={a === 'view' ? true : Boolean(page.caps?.[a])}
          disabled={false}
          onToggle={onToggle}
          isAr={isAr}
        />
      ))}
    </div>
  );
}
